import {
  Card,
  CardContent,
  Box,
  Skeleton,
  Avatar,
  Divider,
  CardHeader,
  Typography,
  Chip,
  LinearProgress,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { CalendarToday } from "@mui/icons-material";
import { ConvertDate } from "../../../Base/helper/baseFunctions";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";

const GoalCard = ({ goal, handleGoalPopup, tab }) => {
  const [anchorE, setAnchorE] = useState(null);
  const open1 = Boolean(anchorE);
  const [currentData, setCurrentData] = useState(null);
  const handleHorMorevertClick = (event, data) => {
    setCurrentData(data);
    setAnchorE(event.currentTarget);
  };
  return (
    <>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: 2,
          minHeight: 170,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Header Section */}
        <CardHeader
          sx={{ py: 1.5, px: 1.5 }}
          title={
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={"-8px"}
            >
              <Chip
                label={goal?.goal_status}
                size="small"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  backgroundColor:
                    goal?.goal_status === "In Progress"
                      ? "#FFF4E5"
                      : goal?.goal_status === "Completed"
                      ? "#E8F5E9"
                      : goal?.goal_status === "Yet to Start"
                      ? "#E3F2FD"
                      : "#E5E7EB",
                  color:
                    goal?.goal_status === "In Progress"
                      ? "#FFA726"
                      : goal?.goal_status === "Completed"
                      ? "#66BB6A"
                      : goal?.goal_status === "Yet to Start"
                      ? "#42A5F5"
                      : "#374151",
                }}
              />
              <Box sx={{display: "flex", alignItems: "center", gap: "10px"}}>
                <Tooltip title={"Preview"}>
                  <VisibilityIcon
                    onClick={() => handleGoalPopup(goal, "Preview")}
                    fontSize="small"
                    sx={{ color: "grey.600", cursor: "pointer" }}
                  />
                </Tooltip>
                {!(tab === "Self" && goal?.alignment === "Team") && (<Tooltip title="More Options">
                    <MoreHorizIcon onClick={(event) => handleHorMorevertClick(event, goal)} fontSize="small" color="action" sx={{ cursor: "pointer"}} />
                </Tooltip>)}
              </Box>
            </Box>
          }
        />

        {/* Title & Description */}
        <CardContent sx={{ py: 0, px: 2 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {goal?.goal_category_name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {goal?.goal_sub_category_name}
          </Typography>

          {/* Progress */}
          <Box mb={1}>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Progress {goal?.weight}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={goal?.weight}
              sx={{
                height: 8,
                borderRadius: 5,
                backgroundColor: "#E5E7EB",
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    goal?.goal_status === "In Progress"
                      ? "#FFA726"
                      : goal?.goal_status === "Completed"
                      ? "#66BB6A"
                      : goal?.goal_status === "Yet to Start"
                      ? "#42A5F5"
                      : "#374151",
                },
              }}
            />
          </Box>
        </CardContent>
        <Divider sx={{ borderBottomWidth: 2, width: "90%", margin: "auto" }} />
        {/* Footer */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          pb={1}
          mt={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ width: 22, height: 22, fontSize: "0.7rem" }}>
              {(goal?.alignment === "Team" && tab === "Self") ? goal?.createdby_name?.[0] : goal?.alignment === "Team" ? goal?.employee_name?.[0] || "U" : "S"}
            </Avatar>
            <Typography variant="body2">
              {(goal?.alignment === "Team" && tab === "Self") ? goal?.createdby_name : goal?.alignment === "Team"
                ? goal?.employee_name || "Unknown"
                : "Self"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <CalendarToday color="disabled" sx={{ width: 20, height: 20 }} />
            <Typography variant="caption">
              {goal?.due_date ? ConvertDate(goal?.due_date) : "-"}
            </Typography>
          </Box>
        </Box>
      </Card>
      <Menu
        anchorEl={anchorE}
        open={open1}
        onClose={() => setAnchorE(null)}
        id="long-menu"
      >
        <MenuItem
          key="Edit"
          onClick={() => handleGoalPopup(currentData, "Edit")}
        >
          <ListItemIcon>
            <DriveFileRenameOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          key="Delete"
          onClick={() => handleGoalPopup(currentData, "Delete")}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export const GoalCardSkeleton = () => {
  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 2,
        height: 200,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <CardContent sx={{ py: 1.5, px: 1.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Skeleton variant="rounded" width={60} height={20} />
          <Skeleton variant="rounded" width={50} height={20} />
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="circular" width={20} height={20} />
        </Box>
      </CardContent>

      <CardContent sx={{ py: 0, px: 2 }}>
        <Skeleton variant="text" width="80%" height={25} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="100%" height={18} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="90%" height={18} />

        <Box mt={1}>
          <Skeleton variant="text" width="40%" height={18} sx={{ mb: 0.5 }} />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={8}
            sx={{ borderRadius: 5 }}
          />
        </Box>
      </CardContent>

      <Divider sx={{ borderBottomWidth: 2, width: "90%", margin: "auto" }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        pb={1.5}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Skeleton variant="circular" width={22} height={22} />
          <Skeleton variant="text" width={50} height={18} />
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={30} height={14} />
        </Box>
      </Box>
    </Card>
  );
};

export default GoalCard;
